import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { shippingAPI } from '../services/api';
import { Spinner } from '../components/Spinner';
import { Plus, Search, Truck, Package, AlertTriangle, BarChart3, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const headerCellClass = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500';
const bodyCellClass = 'px-6 py-4 whitespace-nowrap text-sm text-slate-500';

const ShippingLayout = () => (
  <Routes>
    <Route path="/" element={<ShippingOverview />} />
    <Route path="/shipments" element={<AllShipments />} />
    <Route path="/cargo" element={<CargoShipments />} />
    <Route path="/ambar" element={<AmbarShipments />} />
    <Route path="/rys" element={<RYSShipments />} />
    <Route path="/performance" element={<DeliveryPerformance />} />
    <Route path="/delayed" element={<DelayedShipments />} />
  </Routes>
);

const ShippingOverview = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await shippingAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Sevkiyat istatistikleri alınamadı:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Nakliye</h1>
            <p className="mt-1 text-sm text-slate-500">Sevkiyat tipleri, teslim performansı ve gecikmeleri aynı düzende izleyin.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate('/shipping/shipments')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Sevkiyatlar</button>
            <button onClick={() => navigate('/shipping/performance')} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Performans</button>
            <button onClick={() => navigate('/shipping/delayed')} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">Gecikenler</button>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-100 p-3 text-blue-600"><Package className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Toplam Sevkiyat</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.totalShipments || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-yellow-100 p-3 text-yellow-600"><Clock className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Hazırlanıyor</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.preparingShipments || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-orange-100 p-3 text-orange-600"><Truck className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Yolda</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.shippedShipments || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-green-100 p-3 text-green-600"><BarChart3 className="h-5 w-5" /></div>
            <div>
              <h3 className="text-sm font-medium text-slate-500">Teslim Edildi</h3>
              <p className="text-2xl font-bold text-slate-900">{stats.deliveredShipments || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 text-slate-900">Sevkiyat Türleri</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-slate-500">Kargo</span>
              <span className="text-lg font-semibold text-slate-900">{stats.cargoShipments || 0}</span>
            </div>
            <div className="flex justify-between items-center rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-slate-500">Ambar</span>
              <span className="text-lg font-semibold text-slate-900">{stats.ambarShipments || 0}</span>
            </div>
            <div className="flex justify-between items-center rounded-xl bg-slate-50 px-4 py-3">
              <span className="text-slate-500">RYS</span>
              <span className="text-lg font-semibold text-slate-900">{stats.rysShipments || 0}</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 text-slate-900">Hızlı İşlemler</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <button onClick={() => navigate('/shipping/shipments')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Sevkiyatlar</p><p className="mt-1 text-xs text-slate-500">Tüm kayıtlar</p></button>
            <button onClick={() => navigate('/shipping/performance')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Performans</p><p className="mt-1 text-xs text-slate-500">Teslim oranı</p></button>
            <button onClick={() => navigate('/shipping/delayed')} className="rounded-xl border border-slate-200 px-4 py-4 text-left hover:bg-slate-50"><p className="text-sm font-semibold text-slate-900">Gecikenler</p><p className="mt-1 text-xs text-slate-500">Risk listesi</p></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AllShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchShipments = async () => {
      setLoading(true);
      try {
        const response = await shippingAPI.getShipments();
        setShipments(response.data.shipments || []);
      } catch (error) {
        toast.error('Sevkiyatlar alınamadı');
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, []);

  const filteredShipments = useMemo(
    () =>
      shipments.filter((shipment) =>
        `${shipment.company_name} ${shipment.tracking_number || ''} ${shipment.shipment_type}`.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [shipments, searchTerm]
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Tüm Sevkiyatlar</h1>
          <p className="mt-1 text-sm text-slate-500">Tip, firma ve teslim durumunu sade tablo üzerinde gösterir.</p>
        </div>
        <button data-help-target="shipping-create" className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Sevkiyat Oluştur
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-4">
          <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Sevkiyat ara..."
                className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
          </div>
          <span className="text-sm text-slate-500">{filteredShipments.length} kayıt</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className={headerCellClass}>Tür</th>
                <th className={headerCellClass}>Firma</th>
                <th className={headerCellClass}>Takip No</th>
                <th className={headerCellClass}>Durum</th>
                <th className={headerCellClass}>Sevk Tarihi</th>
                <th className={headerCellClass}>Teslim Tarihi</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredShipments.map((shipment) => (
                <tr key={shipment.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <span className={`rounded-md px-2 py-1 text-xs font-semibold ${
                      shipment.shipment_type === 'Cargo' ? 'bg-blue-100 text-blue-800' :
                      shipment.shipment_type === 'Ambar' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {shipment.shipment_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {shipment.company_name}
                  </td>
                  <td className={bodyCellClass}>
                    {shipment.tracking_number || 'Yok'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${
                      shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                      shipment.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                      shipment.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {shipment.status === 'preparing' ? 'hazırlanıyor' : shipment.status === 'shipped' ? 'yolda' : shipment.status}
                    </span>
                  </td>
                  <td className={bodyCellClass}>
                    {shipment.shipment_date ? new Date(shipment.shipment_date).toLocaleDateString('tr-TR') : 'Yok'}
                  </td>
                  <td className={bodyCellClass}>
                    {shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString('tr-TR') : 'Yok'}
                  </td>
                </tr>
              )) || <tr><td colSpan="6" className="py-4 text-center text-slate-500">Sevkiyat bulunamadı</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CargoShipments = () => <FilteredShipments type="Cargo" />;
const AmbarShipments = () => <FilteredShipments type="Ambar" />;
const RYSShipments = () => <FilteredShipments type="RYS" />;

const FilteredShipments = ({ type }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShipments = async () => {
      setLoading(true);
      try {
        const response = await shippingAPI.getShipments({ shipment_type: type });
        setShipments(response.data.shipments || []);
      } catch (error) {
        toast.error(`${type} sevkiyatları alınamadı`);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [type]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{type} Sevkiyatları</h1>
          <p className="mt-1 text-sm text-slate-500">{type} tipindeki sevkiyatları kart düzeninde takip edin.</p>
        </div>
        <button className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          {type} Sevkiyatı Oluştur
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
        {shipments.map((shipment) => (
          <div key={shipment.id} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{shipment.company_name}</h3>
              <span className={`rounded-md px-2 py-1 text-xs font-semibold ${
                shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                shipment.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                shipment.status === 'preparing' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {shipment.status}
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Takip:</span>
                <span className="font-medium">{shipment.tracking_number || 'Yok'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Sevk Tarihi:</span>
                <span>{shipment.shipment_date ? new Date(shipment.shipment_date).toLocaleDateString('tr-TR') : 'Yok'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Teslim Tarihi:</span>
                <span>{shipment.delivery_date ? new Date(shipment.delivery_date).toLocaleDateString('tr-TR') : 'Yok'}</span>
              </div>
            </div>
            
            {shipment.notes && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <p className="text-sm text-slate-600">{shipment.notes}</p>
              </div>
            )}
          </div>
        )) || <p className="text-slate-500">{type} türünde sevkiyat bulunamadı</p>}
      </div>
    </div>
  );
};

const DeliveryPerformance = () => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const response = await shippingAPI.getDeliveryPerformance();
      setPerformance(response.data);
    } catch (error) {
      toast.error('Teslimat performans verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Teslimat Performansı</h1>
        <p className="mt-1 text-sm text-slate-500">Firma bazlı başarı oranı ve teslim süresi tablosu.</p>
      </div>
      
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className={headerCellClass}>Firma</th>
              <th className={headerCellClass}>Sevkiyat Türü</th>
              <th className={headerCellClass}>Toplam</th>
              <th className={headerCellClass}>Teslim</th>
              <th className={headerCellClass}>Başarı Oranı</th>
              <th className={headerCellClass}>Ort. Teslim Gün</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {performance.map((data, index) => (
              <tr key={index} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                  {data.company_name}
                </td>
                <td className={bodyCellClass}>
                  {data.shipment_type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {data.total_shipments}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {data.delivered_shipments}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex rounded-md px-2 py-1 text-xs font-semibold ${
                    (data.delivered_shipments / data.total_shipments) >= 0.9 ? 'bg-green-100 text-green-800' :
                    (data.delivered_shipments / data.total_shipments) >= 0.7 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {((data.delivered_shipments / data.total_shipments) * 100).toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {data.avg_delivery_days ? `${data.avg_delivery_days.toFixed(1)} gün` : 'Yok'}
                </td>
              </tr>
            )) || <tr><td colSpan="6" className="py-4 text-center text-slate-500">Performans verisi bulunamadı</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DelayedShipments = () => {
  const [delayed, setDelayed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDelayed();
  }, []);

  const fetchDelayed = async () => {
    try {
      const response = await shippingAPI.getDelayedShipments();
      setDelayed(response.data);
    } catch (error) {
      toast.error('Geciken sevkiyatlar alınamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Geciken Sevkiyatlar</h1>
        <p className="mt-1 text-sm text-slate-500">Kritik gecikmeleri kart görünümünde önceliklendirir.</p>
      </div>
      
      {delayed.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {delayed.map((shipment) => (
            <div key={shipment.id} className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  <h3 className="font-semibold text-slate-900">{shipment.company_name}</h3>
                </div>
                <span className={`rounded-md px-2 py-1 text-xs font-semibold ${
                  shipment.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {shipment.status}
                </span>
              </div>
              
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-slate-500">Tür:</span>
                  <span className="font-medium">{shipment.shipment_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Müşteri:</span>
                  <span className="font-medium">{shipment.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Sipariş Tutarı:</span>
                  <span className="font-medium">₺{shipment.order_value?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
              
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm font-semibold text-red-800">
                  {shipment.days_delayed} gün gecikmiş
                </p>
                <p className="text-xs text-red-600">
                  Beklenen: {new Date(shipment.delivery_date).toLocaleDateString('tr-TR')}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <AlertTriangle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Geciken Sevkiyat Yok</h3>
          <p className="text-slate-600">Tüm sevkiyatlar zamanında.</p>
        </div>
      )}
    </div>
  );
};

export default ShippingLayout;
